import * as React from 'react';

import { Button, TextField } from '@rematter/ui-web';
import { GitBranch, Plus, Trash2 } from 'lucide-react';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import { SwitchConfigType, SwitchCaseType } from './schema';

/**
 * Configuration panel for Switch nodes.
 * Allows configuring switch expression and up to 3 cases with labels.
 */
export const SwitchConfig: React.FC<NodeConfigProps<SwitchConfigType>> = ({
  config,
  onUpdate,
}) => {
  const [expression, setExpression] = React.useState<string>(
    config.expression || '',
  );
  const [cases, setCases] = React.useState<SwitchCaseType[]>(
    config.cases || [{ value: '', label: '' }],
  );
  const [defaultLabel, setDefaultLabel] = React.useState<string>(
    config.defaultLabel || 'Default',
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<SwitchConfigType>) => {
      const newConfig = { ...config, ...updates };
      onUpdate(newConfig);
    },
    [config, onUpdate],
  );

  const handleExpressionChange = React.useCallback(
    (value: string) => {
      setExpression(value);
      handleUpdate({ expression: value });
    },
    [handleUpdate],
  );

  const handleDefaultLabelChange = React.useCallback(
    (value: string) => {
      setDefaultLabel(value);
      handleUpdate({ defaultLabel: value });
    },
    [handleUpdate],
  );

  const handleCaseValueChange = React.useCallback(
    (index: number, value: string) => {
      const newCases = [...cases];
      newCases[index] = { ...newCases[index], value };
      setCases(newCases);
      handleUpdate({ cases: newCases });
    },
    [cases, handleUpdate],
  );

  const handleCaseLabelChange = React.useCallback(
    (index: number, label: string) => {
      const newCases = [...cases];
      newCases[index] = { ...newCases[index], label };
      setCases(newCases);
      handleUpdate({ cases: newCases });
    },
    [cases, handleUpdate],
  );

  const handleAddCase = React.useCallback(() => {
    if (cases.length >= 3) return;
    const newCases = [...cases, { value: '', label: '' }];
    setCases(newCases);
    handleUpdate({ cases: newCases });
  }, [cases, handleUpdate]);

  const handleRemoveCase = React.useCallback(
    (index: number) => {
      if (cases.length <= 1) return;
      const newCases = cases.filter((_, i) => i !== index);
      setCases(newCases);
      handleUpdate({ cases: newCases });
    },
    [cases, handleUpdate],
  );

  const canAddCase = cases.length < 3;

  return (
    <div className="@SwitchConfig space-y-4">
      <div>
        <ExpressionInput
          label="Switch Expression"
          value={expression}
          onChange={handleExpressionChange}
          placeholder="trigger.data.status"
          description="Expression that evaluates to a value to match against cases"
        />
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">How Switch Works:</p>
        <div className="space-y-1 text-xs text-neutral/70">
          <div>1. Expression is evaluated to get a raw value</div>
          <div>2. Value is matched against each case value</div>
          <div>3. Flow follows the matching case branch</div>
          <div>4. If no match, flow follows the default branch</div>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitBranch className="size-4 text-neutral/60" />
            <p className="text-sm font-medium">Cases (Max 3)</p>
          </div>
          <Button
            onPress={handleAddCase}
            variant="outline"
            size="small"
            isDisabled={!canAddCase}
          >
            <Plus className="size-4" />
            Add Case
          </Button>
        </div>

        <div className="space-y-3">
          {cases.map((caseItem, index) => (
            <div
              key={index}
              className="space-y-2 rounded-lg border bg-white p-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-neutral/60">
                  Case {index + 1}
                </p>
                {cases.length > 1 && (
                  <Button
                    onPress={() => handleRemoveCase(index)}
                    variant="ghost"
                    size="small"
                  >
                    <Trash2 className="size-4 text-failure" />
                  </Button>
                )}
              </div>

              <TextField
                label="Case Value"
                value={caseItem.value}
                onChange={(value) => handleCaseValueChange(index, value)}
                placeholder="e.g., 'approved', '1', 'high'"
                description="Value to match against the expression result"
              />

              <TextField
                label="Branch Label (Optional)"
                value={caseItem.label || ''}
                onChange={(value) => handleCaseLabelChange(index, value)}
                placeholder={caseItem.value || `Case ${index + 1}`}
                description="Display label for this branch"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="border-t pt-4">
        <TextField
          label="Default Branch Label"
          value={defaultLabel}
          onChange={handleDefaultLabelChange}
          placeholder="Default"
          description="Label for the default branch (when no case matches)"
        />
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Expression Examples:</p>
        <div className="space-y-1 font-mono text-xs text-neutral/70">
          <div>
            <code>trigger.data.status</code> - String matching
          </div>
          <div>
            <code>trigger.data.priority</code> - Number or string
          </div>
          <div>
            <code>trigger.data.type</code> - Enum values
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Access trigger data with{' '}
          <code className="rounded bg-primary/20 px-1">
            trigger.data.fieldName
          </code>{' '}
          and previous action outputs with{' '}
          <code className="rounded bg-primary/20 px-1">
            outputs.nodeId.fieldName
          </code>
          .
        </p>
      </div>

      <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
        <p className="text-xs text-warning">
          <strong>Note:</strong> Unlike Condition which evaluates to true/false,
          Switch matches the raw expression value against case values. Use
          strings with quotes for string matching (e.g., "approved").
        </p>
      </div>
    </div>
  );
};
